import { INTERNAL_NOTIFY_TO } from '../constants/bilditContact.js';

/**
 * User-facing copy when a callable rejects because the BILDIT site id is taken.
 */
export const SITE_NAME_TAKEN_FALLBACK =
  'That BILDIT site name is already in use. Please choose a different site name.';

const BILDIT_SITE_PREFIX = 'bildit-';

/**
 * Strip internal bildit- prefix so signup inputs show what the user typed.
 * @param {string} siteName
 * @returns {string}
 */
export function stripBilditPrefixForDisplay(siteName) {
  if (typeof siteName !== 'string') {
    return '';
  }
  return siteName.startsWith(BILDIT_SITE_PREFIX)
    ? siteName.slice(BILDIT_SITE_PREFIX.length)
    : siteName;
}

/**
 * If err is a "site name already exists / taken" response from createTenant, returns
 * the message to show in the UI; otherwise null. Handles varying Firebase client shapes.
 * @param {unknown} err
 * @returns {string|null}
 */
export function getSiteNameConflictUserMessage(err) {
  if (err == null || typeof err !== 'object') {
    return null;
  }

  const code = err.code != null ? String(err.code) : '';
  const codeLower = code.toLowerCase();
  if (
    codeLower === 'functions/already-exists' ||
    codeLower === 'already-exists' ||
    codeLower.endsWith('/already-exists')
  ) {
    const msg = typeof err.message === 'string' ? err.message.trim() : '';
    return msg || SITE_NAME_TAKEN_FALLBACK;
  }

  const msg = typeof err.message === 'string' ? err.message : '';
  if (
    /already registered/i.test(msg) ||
    /already used on firebase hosting/i.test(msg) ||
    /already in use/i.test(msg) ||
    /site name is already/i.test(msg)
  ) {
    return msg.trim() || SITE_NAME_TAKEN_FALLBACK;
  }

  return null;
}

/**
 * Client timeout / deadline errors where the server may still have finished
 * (e.g. welcome email already sent). Do not treat these as definitive signup_failed.
 * @param {unknown} err
 * @returns {boolean}
 */
export function isAmbiguousSignupClientFailure(err) {
  if (err == null || typeof err !== 'object') {
    return false;
  }

  const code = err.code != null ? String(err.code).toLowerCase() : '';
  if (
    code === 'functions/deadline-exceeded' ||
    code.endsWith('/deadline-exceeded') ||
    code === 'deadline-exceeded'
  ) {
    return true;
  }

  const msg = typeof err.message === 'string' ? err.message.toLowerCase() : '';
  return (
    msg.includes('deadline-exceeded') ||
    msg.includes('deadline exceeded') ||
    /\btimeout\b/.test(msg)
  );
}

const OPAQUE_FIREBASE_MESSAGES =
  /^(INTERNAL|UNKNOWN|UNAVAILABLE|DEADLINE_EXCEEDED|NOT_FOUND|PERMISSION_DENIED|INVALID_ARGUMENT|UNAUTHENTICATED|RESOURCE_EXHAUSTED|FAILED_PRECONDITION|ABORTED|OUT_OF_RANGE|UNIMPLEMENTED|DATA_LOSS)$/i;

/**
 * True when the message is a raw Firebase/gRPC code rather than user copy.
 * @param {string} msg
 * @returns {boolean}
 */
function isOpaqueFirebaseMessage(msg) {
  if (!msg) {
    return true;
  }
  if (OPAQUE_FIREBASE_MESSAGES.test(msg)) {
    return true;
  }
  // e.g. "Firebase: Error (functions/internal)." / "INTERNAL"
  if (/^firebase:/i.test(msg)) {
    return true;
  }
  if (/\(functions\/[a-z-]+\)\.?$/i.test(msg)) {
    return true;
  }
  return false;
}

/**
 * User-facing message when a callable throws (e.g. functions/internal hides server details).
 * @param {unknown} err
 * @param {string} [fallback]
 * @returns {string}
 */
export function getCallableFriendlyErrorMessage(
  err,
  fallback = 'Something went wrong. Please try again.'
) {
  const conflict = getSiteNameConflictUserMessage(err);
  if (conflict) {
    return conflict;
  }

  if (err == null || typeof err !== 'object') {
    return fallback;
  }

  const code = err.code != null ? String(err.code) : '';
  const codeLower = code.toLowerCase();
  const msg = typeof err.message === 'string' ? err.message.trim() : '';

  if (
    codeLower === 'functions/internal' ||
    codeLower.endsWith('/internal') ||
    OPAQUE_FIREBASE_MESSAGES.test(msg) ||
    (/internal/i.test(msg) && /functions\/internal/i.test(`${code} ${msg}`))
  ) {
    return (
      "We couldn't complete that request right now. Please try again in a moment. " +
      `If it keeps happening, contact ${INTERNAL_NOTIFY_TO}.`
    );
  }

  if (codeLower === 'functions/not-found' || codeLower.endsWith('/not-found')) {
    return (
      'That feature is not available on this deployment yet. ' +
      `Contact ${INTERNAL_NOTIFY_TO} if you need help.`
    );
  }

  if (
    codeLower === 'functions/unavailable' ||
    codeLower.endsWith('/unavailable')
  ) {
    if (msg && !isOpaqueFirebaseMessage(msg)) {
      return msg;
    }
    return 'Service is temporarily busy. Please try again shortly.';
  }

  if (
    codeLower === 'functions/deadline-exceeded' ||
    codeLower.endsWith('/deadline-exceeded')
  ) {
    return 'That took too long. Please try again.';
  }

  if (msg && !isOpaqueFirebaseMessage(msg)) {
    return msg;
  }
  return fallback;
}
